import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { PhaseSection } from "@/components/admin/estimates/PhaseSection";
import { MeasurementSummarySection } from "@/components/admin/estimates/MeasurementSummarySection";
import { groupLineItemsByPhase } from "@/utils/proposalMapping";
import { INSTALLATION_PHASES } from "@/utils/installationPhases";
import { WARRANTY_CONTENT } from "@/utils/proposalContent";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CustomerProposalGallery } from "@/components/admin/estimates/CustomerProposalGallery";
import { SignatureDialog } from "@/components/admin/estimates/SignatureDialog";
import { ChangeRequestDialog } from "@/components/admin/estimates/ChangeRequestDialog";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, Download, Edit, Phone, Mail, MapPin, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import "@/styles/proposal-print.css";

export default function PublicProposalView() {
  const { estimateId, accessToken } = useParams();
  const [estimate, setEstimate] = useState<any>(null);
  const [lineItems, setLineItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [customizeMode, setCustomizeMode] = useState(false);
  const [tierDefaultsMap, setTierDefaultsMap] = useState<Record<string, any[]>>({});
  const [isLoadingTiers, setIsLoadingTiers] = useState(false);
  const [isSwitchingTier, setIsSwitchingTier] = useState(false);
  const [originalEstimate, setOriginalEstimate] = useState<any>(null);
  const [changes, setChanges] = useState<Array<{
    phaseKey: string;
    phaseName: string;
    fromProduct: string;
    toProduct: string;
    fromTier: string;
    toTier: string;
    priceDifference: number;
  }>>([]);

  useEffect(() => {
    if (estimateId && accessToken) {
      loadProposal();
      trackView();
    }
  }, [estimateId, accessToken]);

  const loadProposal = async () => {
    try {
      // Verify access token and check expiration
      const { data: estimateData, error: estimateError } = await supabase
        .from("estimates")
        .select("*")
        .eq("id", estimateId)
        .eq("public_access_token", accessToken)
        .eq("public_access_enabled", true)
        .single();

      if (estimateError || !estimateData) {
        toast.error("Invalid or expired proposal link");
        setIsLoading(false);
        return;
      }

      // Check if link has expired
      if (estimateData.public_access_expires_at) {
        const expirationDate = new Date(estimateData.public_access_expires_at);
        if (expirationDate < new Date()) {
          toast.error("This proposal link has expired");
          setIsLoading(false);
          return;
        }
      }

      setEstimate(estimateData);
      
      // Save original estimate on first load
      if (!originalEstimate) {
        setOriginalEstimate({
          subtotal: estimateData.subtotal,
          tax_amount: estimateData.tax_amount,
          total: estimateData.total
        });
      }

      // Load line items
      const { data: items, error: itemsError } = await supabase
        .from("estimate_line_items")
        .select("*, products(*)")
        .eq("estimate_id", estimateId)
        .order("sort_order");

      if (!itemsError && items) {
        setLineItems(items);
      }
    } catch (error) {
      console.error("Error loading proposal:", error);
      toast.error("Failed to load proposal");
    } finally {
      setIsLoading(false);
    }
  };

  const trackView = async () => {
    try {
      // Get current view count
      const { data } = await supabase
        .from("estimates")
        .select("view_count")
        .eq("id", estimateId)
        .single();

      await supabase
        .from("estimates")
        .update({
          view_count: (data?.view_count || 0) + 1,
          last_viewed_at: new Date().toISOString()
        })
        .eq("id", estimateId);
    } catch (error) {
      console.error("Error tracking view:", error);
    }
  };

  const handleSign = async (signatureData: string, signatureName: string) => {
    try {
      // Get IP address (simplified version)
      const ipResponse = await fetch("https://api.ipify.org?format=json");
      const ipData = await ipResponse.json();

      const { error } = await supabase
        .from("estimates")
        .update({
          customer_signature_data: signatureData,
          customer_signature_date: new Date().toISOString(),
          signature_name: signatureName,
          customer_ip_address: ipData.ip,
          accepted_at: new Date().toISOString(),
          status: "accepted"
        })
        .eq("id", estimateId);

      if (error) throw error;

      // Reload to show updated status
      await loadProposal();
    } catch (error) {
      console.error("Error saving signature:", error);
      throw error;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Reusable function to load tier defaults
  const loadTierDefaults = async () => {
    if (!estimate?.price_book_id) return;

    setIsLoadingTiers(true);
    try {
      // Single query to get ALL tier defaults with products
      const { data: allTierDefaults, error: tierError } = await supabase
        .from('phase_tier_defaults')
        .select('*, product:products(*)')
        .order('phase_key');

      if (tierError) throw tierError;
      if (!allTierDefaults || allTierDefaults.length === 0) {
        setIsLoadingTiers(false);
        return;
      }

      // Get all unique product IDs
      const productIds = allTierDefaults.map(td => td.product_id);

      // Single query to get ALL prices
      const { data: allPrices, error: priceError } = await supabase
        .from('product_prices')
        .select('product_id, unit_price')
        .eq('price_book_id', estimate.price_book_id)
        .in('product_id', productIds);

      if (priceError) throw priceError;

      // Group by phase_key
      const tierMap: Record<string, any[]> = {};
      allTierDefaults.forEach(tier => {
        if (!tierMap[tier.phase_key]) {
          tierMap[tier.phase_key] = [];
        }
        
        const price = allPrices?.find(p => p.product_id === tier.product_id);
        tierMap[tier.phase_key].push({
          ...tier,
          unit_price: price?.unit_price
        });
      });

      setTierDefaultsMap(tierMap);
    } catch (error) {
      console.error('Error loading tier defaults:', error);
      toast.error('Failed to load customization options');
    } finally {
      setIsLoadingTiers(false);
    }
  };

  // Load tier defaults when customization mode is enabled
  useEffect(() => {
    if (!customizeMode || !estimate?.price_book_id) {
      setTierDefaultsMap({});
      return;
    }

    loadTierDefaults();
  }, [customizeMode, estimate?.price_book_id]);

  // Handle tier change
  const handleTierChange = async (
    tier: 'good' | 'better' | 'best',
    phaseKey: string,
    newProductId: string
  ) => {
    if (isSwitchingTier) return; // Prevent double-clicks
    
    setIsSwitchingTier(true);
    try {
      // Find the line item that belongs to this tier group
      const phaseTiers = tierDefaultsMap[phaseKey] || [];
      const existingItem = lineItems.find(item => {
        // Check if this item's product is in the tier defaults for this phase
        return phaseTiers.some(td => td.product_id === item.product_id);
      });

      if (!existingItem) {
        toast.error("Could not find line item for this phase");
        return;
      }

      // Get OLD tier info for tracking
      const oldTier = phaseTiers.find(td => td.product_id === existingItem.product_id);
      const oldPrice = existingItem.line_total;

      // Get new product details
      const { data: newProduct, error: productError } = await supabase
        .from('products')
        .select('*')
        .eq('id', newProductId)
        .single();

      if (productError || !newProduct) {
        toast.error("Could not load product details");
        return;
      }

      // Get price from price book
      const { data: priceData, error: priceError } = await supabase
        .from('product_prices')
        .select('unit_price')
        .eq('product_id', newProductId)
        .eq('price_book_id', estimate.price_book_id)
        .single();

      if (priceError || !priceData) {
        toast.error("Could not load product price");
        return;
      }

      // Calculate new line total
      const newLineTotal = existingItem.quantity * priceData.unit_price;
      const priceDifference = newLineTotal - oldPrice;

      // Update line item
      const { error: updateError } = await supabase
        .from('estimate_line_items')
        .update({
          product_id: newProductId,
          description: newProduct.name,
          unit_price: priceData.unit_price,
          line_total: newLineTotal,
        })
        .eq('id', existingItem.id);

      if (updateError) {
        toast.error("Failed to update line item");
        return;
      }

      // Track the change
      const phase = INSTALLATION_PHASES.find(p => p.key === phaseKey);
      setChanges(prev => {
        // Remove any existing change for this phase
        const filtered = prev.filter(c => c.phaseKey !== phaseKey);
        // Add new change
        return [...filtered, {
          phaseKey,
          phaseName: phase?.title || phaseKey,
          fromProduct: existingItem.description,
          toProduct: newProduct.name,
          fromTier: oldTier?.tier || 'unknown',
          toTier: tier,
          priceDifference
        }];
      });

      // Recalculate and update estimate totals
      const { data: allItems } = await supabase
        .from('estimate_line_items')
        .select('line_total')
        .eq('estimate_id', estimateId);

      if (allItems) {
        const newSubtotal = allItems.reduce((sum, item) => sum + (item.line_total || 0), 0);
        const newTaxAmount = newSubtotal * ((estimate.tax_rate || 0) / 100);
        const newTotal = newSubtotal + newTaxAmount;

        await supabase
          .from('estimates')
          .update({
            subtotal: newSubtotal,
            tax_amount: newTaxAmount,
            total: newTotal,
          })
          .eq('id', estimateId);
      }

      // Reload entire proposal to ensure UI is fresh
      await loadProposal();
      
      // Reload tier defaults so getCurrentTier() can match the new product
      if (customizeMode) {
        await loadTierDefaults();
      }
      
      toast.success(`Switched to ${tier.toUpperCase()} tier`);
    } catch (error) {
      console.error("Error changing tier:", error);
      toast.error("Failed to change tier - please try again");
    } finally {
      setIsSwitchingTier(false);
    }
  };

  const groupedItems = lineItems ? groupLineItemsByPhase(lineItems) : {};

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-muted-foreground">Loading proposal...</p>
        </div>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Proposal Not Found</h2>
          <p className="text-muted-foreground">This proposal link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  const isAccepted = !!estimate.accepted_at;

  return (
    <div className="min-h-screen bg-background">
      {/* Proposal Content */}
      <div className="proposal-container max-w-5xl mx-auto p-8 bg-background">
        {/* Header with Logo */}
        <div className="mb-8 page-break-inside-avoid">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-4">
              <img 
                src="/assets/logos/safeway-roofing-logo.png" 
                alt="Safeway Home Improvements" 
                className="h-16 md:h-20 w-auto"
              />
            </div>
            <div className="text-left md:text-right">
              <p className="text-sm text-muted-foreground">Estimate #</p>
              <p className="text-lg font-bold">{estimate.estimate_number}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(estimate.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <Separator className="my-6" />

          {/* Customer Information */}
          <div>
            <h2 className="text-xl font-bold text-foreground mb-4">Proposal For:</h2>
            <div className="space-y-2">
              <p className="text-lg font-semibold">{estimate.customer_name}</p>
              {estimate.customer_phone && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  {estimate.customer_phone}
                </p>
              )}
              {estimate.customer_email && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  {estimate.customer_email}
                </p>
              )}
              {estimate.customer_address && (
                <p className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {estimate.customer_address}, {estimate.city}, {estimate.zip_code}
                </p>
              )}
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {isAccepted && (
          <div className="mb-8 p-4 bg-primary/10 border border-primary rounded-lg">
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle className="h-5 w-5" />
              <span className="font-semibold">
                This proposal was accepted on {new Date(estimate.accepted_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {/* Executive Summary */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">Project Overview</h2>
          <p className="text-muted-foreground leading-relaxed">
            This comprehensive roofing proposal outlines the complete scope of work, materials, and 
            professional installation process for your project. We use only premium materials and 
            proven installation techniques to ensure lasting protection for your home.
          </p>
        </div>

        {/* Measurement Report */}
        {estimate.measurement_data && (
          <MeasurementSummarySection
            measurementData={estimate.measurement_data}
            measurementSource={estimate.measurement_source}
            fileUrl={estimate.measurement_file_url}
            pdfUrl={estimate.measurement_pdf_url}
          />
        )}

        <Separator className="my-8" />

        {/* Installation Process */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-foreground">
              Installation Process & Materials
            </h2>
            
            {!isAccepted && (
              <Button
                variant={customizeMode ? "default" : "outline"}
                size="lg"
                onClick={() => setCustomizeMode(!customizeMode)}
                disabled={isLoadingTiers}
                className="no-print"
              >
                {isLoadingTiers ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading options...
                  </>
                ) : (
                  customizeMode ? "Lock Selections" : "Customize Your Estimate"
                )}
              </Button>
            )}
          </div>
          
          {INSTALLATION_PHASES.map(phase => {
            const phaseItems = groupedItems[phase.key];
            if (!phaseItems || phaseItems.length === 0) return null;
            
            return (
              <PhaseSection
                key={phase.key}
                phase={phase}
                lineItems={phaseItems}
                showImages={true}
                showTierSelector={customizeMode}
                tierDefaults={tierDefaultsMap[phase.key] || []}
                onTierChange={(tier, productId) => handleTierChange(tier, phase.key, productId)}
              />
            );
          })}
        </div>

        <Separator className="my-8" />

        {/* Investment Summary */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-foreground mb-4">Investment Summary</h2>
          
          {customizeMode && changes.length > 0 ? (
            <div className="space-y-6">
              {/* Original vs Current Comparison */}
              <div className="bg-muted/30 rounded-lg p-6 border">
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-lg">
                    <span className="font-medium text-muted-foreground">Original Total:</span>
                    <span className="font-semibold text-muted-foreground line-through">
                      ${originalEstimate?.total?.toLocaleString() || '0'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-2xl">
                    <span className="font-bold text-foreground">Updated Total:</span>
                    <span className="font-bold text-primary">
                      ${estimate.total?.toLocaleString() || '0'}
                    </span>
                  </div>
                  
                  {/* Show difference */}
                  {estimate.total && originalEstimate?.total && estimate.total !== originalEstimate.total && (
                    <div className="flex justify-between items-center text-lg pt-4 border-t">
                      <span className="font-medium text-foreground">
                        Price Difference:
                      </span>
                      <span className={cn(
                        "font-bold flex items-center gap-2",
                        estimate.total > originalEstimate.total ? "text-amber-600" : "text-green-600"
                      )}>
                        {estimate.total > originalEstimate.total ? (
                          <>
                            <TrendingUp className="w-5 h-5" />
                            +${Math.abs(estimate.total - originalEstimate.total).toLocaleString()}
                            <span className="text-sm font-normal">(increase)</span>
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-5 h-5" />
                            -${Math.abs(estimate.total - originalEstimate.total).toLocaleString()}
                            <span className="text-sm font-normal">(savings)</span>
                          </>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Changes Summary */}
              <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-6 border border-blue-200 dark:border-blue-900">
                <h3 className="text-xl font-semibold text-foreground mb-4">Your Customizations</h3>
                <div className="space-y-3">
                  {changes.map((change, idx) => (
                    <div key={idx} className="flex justify-between items-start pb-3 border-b border-blue-200 dark:border-blue-900 last:border-0">
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{change.phaseName}</p>
                        <p className="text-sm text-muted-foreground">
                          <span className="line-through">{change.fromProduct}</span>
                          <span className="mx-2">→</span>
                          <span className="font-medium text-foreground">{change.toProduct}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {change.fromTier.toUpperCase()} tier → {change.toTier.toUpperCase()} tier
                        </p>
                      </div>
                      <div className={cn(
                        "font-semibold text-right ml-4",
                        change.priceDifference > 0 ? "text-amber-600" : "text-green-600"
                      )}>
                        {change.priceDifference > 0 ? "+" : ""}
                        ${change.priceDifference.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Finalize Button */}
              <div className="flex justify-center">
                <Button 
                  size="lg" 
                  onClick={() => {
                    setCustomizeMode(false);
                    toast.success("Selections finalized! Review your customized proposal below.");
                  }}
                  className="px-8"
                >
                  Finalize Selections
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-muted/30 rounded-lg p-6 border">
              <div className="flex justify-between items-center text-xl">
                <span className="font-medium text-foreground">Total Investment:</span>
                <span className="font-bold text-primary">
                  ${estimate.total?.toLocaleString() || '0'}
                </span>
              </div>
            </div>
          )}
        </div>

        <Separator className="my-8" />

        {/* Warranty Section */}
        <div className="mb-12 page-break-inside-avoid">
          <h2 className="text-2xl font-bold text-foreground mb-6">Warranty Information</h2>
          
          <div className="space-y-8">
            <div className="bg-muted/30 rounded-lg p-6 border">
              <h3 className="text-xl font-semibold text-foreground mb-3 flex items-center">
                <CheckCircle className="w-5 h-5 text-primary mr-2 flex-shrink-0" />
                {WARRANTY_CONTENT.workmanship.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {WARRANTY_CONTENT.workmanship.description}
              </p>
            </div>

            <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
              <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center">
                <CheckCircle className="w-5 h-5 text-primary mr-2 flex-shrink-0" />
                {WARRANTY_CONTENT.manufacturer.title}
              </h3>
              <ul className="space-y-3">
                {WARRANTY_CONTENT.manufacturer.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground leading-relaxed">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Customer Photos or Project Gallery */}
        <CustomerProposalGallery 
          customerPhotos={estimate.project_photos?.filter((p: any) => p.include_in_proposal !== false)}
          fallbackToGeneric={true}
        />

        {/* Interactive Footer */}
        <div className="mt-12 pt-8 border-t no-print">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Ready to Get Started?</h2>
            <p className="text-muted-foreground">
              Review the proposal above and take action below.
            </p>
          </div>

          {!isAccepted ? (
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button size="lg" onClick={() => setSignatureDialogOpen(true)}>
                <CheckCircle className="mr-2 h-5 w-5" />
                Accept & Sign Proposal
              </Button>
              <Button size="lg" variant="outline" onClick={() => setChangeDialogOpen(true)}>
                <Edit className="mr-2 h-5 w-5" />
                Request Changes
              </Button>
              <Button size="lg" variant="outline" onClick={handlePrint}>
                <Download className="mr-2 h-5 w-5" />
                Download PDF
              </Button>
            </div>
          ) : (
            <div className="text-center mb-8">
              <p className="text-lg font-semibold text-primary mb-4">
                Thank you for accepting this proposal!
              </p>
              <Button size="lg" variant="outline" onClick={handlePrint}>
                <Download className="mr-2 h-5 w-5" />
                Download PDF
              </Button>
            </div>
          )}

          <div className="text-center text-sm text-muted-foreground">
            <p className="font-semibold mb-2">Questions? Contact us:</p>
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <a href="tel:+15551234567" className="flex items-center gap-1 hover:text-primary">
                <Phone className="h-4 w-4" />
                (555) 123-4567
              </a>
              <a href="mailto:info@safewayroofing.com" className="flex items-center gap-1 hover:text-primary">
                <Mail className="h-4 w-4" />
                info@safewayroofing.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <SignatureDialog
        open={signatureDialogOpen}
        onClose={() => setSignatureDialogOpen(false)}
        onSign={handleSign}
        estimateTotal={estimate?.total || 0}
        customerName={estimate?.customer_name || ""}
      />

      <ChangeRequestDialog
        open={changeDialogOpen}
        onClose={() => setChangeDialogOpen(false)}
        estimateId={estimateId || ""}
        customerName={estimate?.customer_name || ""}
        customerEmail={estimate?.customer_email}
        customerPhone={estimate?.customer_phone || ""}
      />
    </div>
  );
}
